import { useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { BLOCK_DEFINITIONS, defaultContent } from '../blockTypes';
import { DATA_REFERENCE_TOKENS, TextFormatButtons, TokenInsertButtons, appendToken } from './tokenTools';
import IconClassPicker from './IconClassPicker';

const HERO_LAYER_LIMIT = 3;
const REF_TOKEN_DATALIST_ID = 'pagebuilder-reference-tokens';
const API_BASE = process.env.REACT_APP_URL || 'http://localhost:3501';
const DATA_CARD_BLOCK_OPTIONS = BLOCK_DEFINITIONS
  .map((def) => ({
    value: def.type,
    label: def.type === 'html' ? 'Raw HTML (span/div/etc.)' : def.label,
  }));
const makeDataCardBlock = (type = 'paragraph') => ({
  id: `dcb-${Date.now()}-${Math.floor(Math.random() * 10000)}`,
  type,
  content: type === 'html' ? { html: '' } : defaultContent(type),
});
const HEADING_LEVELS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];
const BOOTSTRAP_VARIANTS = ['primary', 'secondary', 'success', 'danger', 'warning', 'info', 'light', 'dark'];

const clamp = (num, min, max) => Math.min(max, Math.max(min, num));

const makeHeroLayer = () => ({
  id: `hl-${Date.now()}-${Math.floor(Math.random() * 10000)}`,
  url: '',
  x: 70,
  y: 55,
  width: 24,
  opacity: 1,
});

function usePageBuilderPages() {
  const [pages, setPages] = useState([]);

  useEffect(() => {
    let mounted = true;

    const loadPages = async () => {
      try {
        const response = await fetch(`${API_BASE}/pagebuilder/pages`, {
          headers: { 'Content-Type': 'application/json' },
        });
        if (!response.ok) return;

        const data = await response.json();
        if (!mounted || !Array.isArray(data)) return;
        setPages(data);
      } catch {
        // Ignore helper loading issues; manual URLs still work.
      }
    };

    loadPages();
    return () => {
      mounted = false;
    };
  }, []);

  return useMemo(
    () => pages.map((page) => ({ id: page.id, label: page.title || page.name || page.id })),
    [pages]
  );
}

function getPointerPosition(event) {
  if (event.touches && event.touches[0]) {
    return { clientX: event.touches[0].clientX, clientY: event.touches[0].clientY };
  }
  return { clientX: event.clientX, clientY: event.clientY };
}

function HeroImageBuilder({ content, update }) {
  const layers = useMemo(
    () => (Array.isArray(content.heroLayers) ? content.heroLayers.slice(0, HERO_LAYER_LIMIT) : []),
    [content.heroLayers]
  );
  const [selectedLayerId, setSelectedLayerId] = useState(layers[0]?.id || null);
  const [dragState, setDragState] = useState(null);
  const canvasRef = useRef(null);
  const selectedLayer = layers.find((layer) => layer.id === selectedLayerId) || null;

  useEffect(() => {
    if (!layers.length) {
      setSelectedLayerId(null);
      return;
    }
    if (selectedLayerId && !layers.some((layer) => layer.id === selectedLayerId)) {
      setSelectedLayerId(layers[0].id);
    }
  }, [layers, selectedLayerId]);

  useEffect(() => {
    if (!dragState) return undefined;

    const handlePointerMove = (event) => {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;
      if (event.touches) event.preventDefault();

      const pointer = getPointerPosition(event);
      const deltaX = ((pointer.clientX - dragState.startX) / rect.width) * 100;
      const deltaY = ((pointer.clientY - dragState.startY) / rect.height) * 100;

      update('heroLayers', layers.map((layer) => {
        if (layer.id !== dragState.layerId) return layer;

        if (dragState.mode === 'resize') {
          const directionalDelta = ((deltaX * dragState.dirX) + (deltaY * dragState.dirY)) / 2;
          const nextWidth = clamp(dragState.originWidth + (directionalDelta * 2), 8, 95);
          return { ...layer, width: Number(nextWidth.toFixed(1)) };
        }

        const nextX = clamp(dragState.originX + deltaX, 0, 100);
        const nextY = clamp(dragState.originY + deltaY, 0, 100);
        return { ...layer, x: Number(nextX.toFixed(1)), y: Number(nextY.toFixed(1)) };
      }));
    };

    const handlePointerUp = () => setDragState(null);

    window.addEventListener('mousemove', handlePointerMove);
    window.addEventListener('mouseup', handlePointerUp);
    window.addEventListener('touchmove', handlePointerMove, { passive: false });
    window.addEventListener('touchend', handlePointerUp);

    return () => {
      window.removeEventListener('mousemove', handlePointerMove);
      window.removeEventListener('mouseup', handlePointerUp);
      window.removeEventListener('touchmove', handlePointerMove);
      window.removeEventListener('touchend', handlePointerUp);
    };
  }, [dragState, layers, update]);

  const addLayer = () => {
    if (layers.length >= HERO_LAYER_LIMIT) return;
    const next = [...layers, makeHeroLayer()];
    update('heroLayers', next);
    setSelectedLayerId(next[next.length - 1].id);
  };

  const removeLayer = (layerId) => {
    const next = layers.filter((layer) => layer.id !== layerId);
    update('heroLayers', next);
    if (selectedLayerId === layerId) setSelectedLayerId(next[0]?.id || null);
  };

  const duplicateLayer = (layerId) => {
    if (layers.length >= HERO_LAYER_LIMIT) return;
    const source = layers.find((layer) => layer.id === layerId);
    if (!source) return;

    const clone = {
      ...source,
      id: `hl-${Date.now()}-${Math.floor(Math.random() * 10000)}`,
      x: clamp(Number(source.x || 0) + 4, 0, 100),
      y: clamp(Number(source.y || 0) + 4, 0, 100),
    };
    const next = [...layers, clone];
    update('heroLayers', next);
    setSelectedLayerId(clone.id);
  };

  const moveLayerOrder = (layerId, direction) => {
    const index = layers.findIndex((layer) => layer.id === layerId);
    if (index < 0) return;

    const target = clamp(index + direction, 0, layers.length - 1);
    if (target === index) return;

    const next = [...layers];
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item);
    update('heroLayers', next);
  };

  const updateLayer = (layerId, field, value) => {
    update('heroLayers', layers.map((layer) => (layer.id === layerId ? { ...layer, [field]: value } : layer)));
  };

  const nudgeSelectedOpacity = (delta) => {
    if (!selectedLayer) return;
    const nextOpacity = clamp(Number(selectedLayer.opacity ?? 1) + delta, 0.1, 1);
    updateLayer(selectedLayer.id, 'opacity', Number(nextOpacity.toFixed(2)));
  };

  const startDrag = (event, layer) => {
    event.preventDefault();
    const pointer = getPointerPosition(event);
    setSelectedLayerId(layer.id);
    setDragState({
      mode: 'move',
      layerId: layer.id,
      startX: pointer.clientX,
      startY: pointer.clientY,
      originX: Number(layer.x || 0),
      originY: Number(layer.y || 0),
      originWidth: Number(layer.width || 24),
      dirX: 1,
      dirY: 1,
    });
  };

  const startResize = (event, layer, dirX, dirY) => {
    event.preventDefault();
    event.stopPropagation();
    const pointer = getPointerPosition(event);
    setSelectedLayerId(layer.id);
    setDragState({
      mode: 'resize',
      layerId: layer.id,
      startX: pointer.clientX,
      startY: pointer.clientY,
      originX: Number(layer.x || 0),
      originY: Number(layer.y || 0),
      originWidth: Number(layer.width || 24),
      dirX,
      dirY,
    });
  };

  const handleCanvasPointerDown = (event) => {
    if (event.target === event.currentTarget) {
      setSelectedLayerId(null);
    }
  };

  return (
    <div className="pagebuilder-hero-composer mb-3">
      <div className="pagebuilder-field-row mb-2">
        <Form.Group style={{ flex: '0 0 180px' }}>
          <Form.Label>Hero background color</Form.Label>
          <Form.Control
            type="color"
            value={content.heroBackgroundColor || '#001b34'}
            onChange={(e) => update('heroBackgroundColor', e.target.value)}
          />
        </Form.Group>
        <div className="pagebuilder-hero-layer-actions">
          <button
            type="button"
            className="pagebuilder-add-btn pagebuilder-add-btn--sm"
            onClick={addLayer}
            disabled={layers.length >= HERO_LAYER_LIMIT}
          >
            <i className="bi bi-plus-lg" aria-hidden="true" /> Add PNG Layer ({layers.length}/{HERO_LAYER_LIMIT})
          </button>
        </div>
      </div>

      <Form.Text className="pagebuilder-help-note">
        Drag layers to move. Use square corner handles to resize directly on canvas.
      </Form.Text>

      <div
        className="pagebuilder-hero-canvas"
        ref={canvasRef}
        style={{ backgroundColor: content.heroBackgroundColor || '#001b34' }}
        onMouseDown={handleCanvasPointerDown}
        onTouchStart={handleCanvasPointerDown}
      >
        {layers.length === 0 && <span className="pagebuilder-hero-canvas-empty">No layers yet. Add up to 3 PNG layers.</span>}

        {selectedLayer && (
          <div className="pagebuilder-hero-canvas-tools">
            <button type="button" className="pagebuilder-block-action-btn" onClick={() => moveLayerOrder(selectedLayer.id, -1)} title="Send backward">
              <i className="bi bi-arrow-down" />
            </button>
            <button type="button" className="pagebuilder-block-action-btn" onClick={() => moveLayerOrder(selectedLayer.id, 1)} title="Bring forward">
              <i className="bi bi-arrow-up" />
            </button>
            <div className="pagebuilder-hero-opacity-inline" title="Layer opacity">
              <button type="button" className="pagebuilder-block-action-btn" onClick={() => nudgeSelectedOpacity(-0.05)} aria-label="Decrease opacity">
                <i className="bi bi-dash-lg" />
              </button>
              <span>{Math.round(Number(selectedLayer.opacity ?? 1) * 100)}%</span>
              <button type="button" className="pagebuilder-block-action-btn" onClick={() => nudgeSelectedOpacity(0.05)} aria-label="Increase opacity">
                <i className="bi bi-plus-lg" />
              </button>
            </div>
            <button type="button" className="pagebuilder-block-action-btn" onClick={() => duplicateLayer(selectedLayer.id)} disabled={layers.length >= HERO_LAYER_LIMIT} title="Duplicate">
              <i className="bi bi-files" />
            </button>
            <button type="button" className="pagebuilder-block-action-btn pagebuilder-block-action-btn--danger" onClick={() => removeLayer(selectedLayer.id)} title="Delete">
              <i className="bi bi-trash" />
            </button>
          </div>
        )}

        {layers.map((layer, idx) => (
          <div
            key={layer.id}
            className={`pagebuilder-hero-layer ${selectedLayerId === layer.id ? 'is-selected' : ''}`}
            style={{
              left: `${Number(layer.x || 0)}%`,
              top: `${Number(layer.y || 0)}%`,
              width: `${Number(layer.width || 24)}%`,
              zIndex: idx + 1,
            }}
            onMouseDown={(event) => startDrag(event, layer)}
            onTouchStart={(event) => startDrag(event, layer)}
            onClick={() => setSelectedLayerId(layer.id)}
          >
            <img
              src={layer.url || ''}
              alt=""
              draggable={false}
              className="pagebuilder-hero-layer-image"
              style={{ opacity: Number(layer.opacity ?? 1) }}
            />

            {selectedLayerId === layer.id && (
              <>
                <button type="button" className="pagebuilder-hero-layer-handle pagebuilder-hero-layer-handle--nw" onMouseDown={(event) => startResize(event, layer, -1, -1)} onTouchStart={(event) => startResize(event, layer, -1, -1)} aria-label="Resize from top left" />
                <button type="button" className="pagebuilder-hero-layer-handle pagebuilder-hero-layer-handle--ne" onMouseDown={(event) => startResize(event, layer, 1, -1)} onTouchStart={(event) => startResize(event, layer, 1, -1)} aria-label="Resize from top right" />
                <button type="button" className="pagebuilder-hero-layer-handle pagebuilder-hero-layer-handle--sw" onMouseDown={(event) => startResize(event, layer, -1, 1)} onTouchStart={(event) => startResize(event, layer, -1, 1)} aria-label="Resize from bottom left" />
                <button type="button" className="pagebuilder-hero-layer-handle pagebuilder-hero-layer-handle--se" onMouseDown={(event) => startResize(event, layer, 1, 1)} onTouchStart={(event) => startResize(event, layer, 1, 1)} aria-label="Resize from bottom right" />
              </>
            )}
          </div>
        ))}
      </div>

      <div className="pagebuilder-hero-layers-grid mt-2">
        {layers.map((layer, idx) => (
          <div key={layer.id} className="pagebuilder-collage-item">
            <div className="pagebuilder-collage-item-header">
              <span className="pagebuilder-sublist-index">Layer {idx + 1}</span>
              <button
                type="button"
                className="pagebuilder-block-action-btn pagebuilder-block-action-btn--danger"
                onClick={() => removeLayer(layer.id)}
                title="Remove layer"
              >
                <i className="bi bi-trash" />
              </button>
            </div>
            <Form.Group className="mb-2">
              <Form.Label>PNG URL</Form.Label>
              <Form.Control
                size="sm"
                value={layer.url || ''}
                onChange={(e) => updateLayer(layer.id, 'url', e.target.value)}
                placeholder="https://…"
              />
            </Form.Group>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Shared nested-block field editor ────────────────────────────────────────
// Used by both the dataCard and accordion cases so every block type has a
// proper UI when embedded as a nested block.
function NestedBlockFields({ type, content, blockId, onUpdate, onFocus }) {
  const pageOptions = usePageBuilderPages();
  const buttonLabelInputRef = useRef(null);
  const buttonLabelSelectionRef = useRef({ start: null, end: null });

  const captureButtonLabelSelection = (event) => {
    const target = event?.target;
    if (!target) return;
    buttonLabelSelectionRef.current = {
      start: target.selectionStart,
      end: target.selectionEnd,
    };
  };

  const insertIconClassIntoButtonLabel = (iconClass) => {
    if (!iconClass) return;

    const currentLabel = content?.label || '';
    const iconMarkup = `<i class="${iconClass}"></i>`;
    const { start, end } = buttonLabelSelectionRef.current || {};
    const hasSelection = Number.isInteger(start) && Number.isInteger(end);

    if (!hasSelection) {
      const spacer = currentLabel && !/\s$/.test(currentLabel) ? ' ' : '';
      onUpdate('label', `${currentLabel}${spacer}${iconMarkup}`);
      return;
    }

    const prefix = currentLabel.slice(0, start);
    const suffix = currentLabel.slice(end);
    const needsLeadingSpace = prefix && !/\s$/.test(prefix);
    const needsTrailingSpace = suffix && !/^\s/.test(suffix);
    const inserted = `${needsLeadingSpace ? ' ' : ''}${iconMarkup}${needsTrailingSpace ? ' ' : ''}`;
    const nextLabel = `${prefix}${inserted}${suffix}`;
    const caretPos = prefix.length + inserted.length;

    onUpdate('label', nextLabel);

    window.requestAnimationFrame(() => {
      const input = buttonLabelInputRef.current;
      if (!input) return;
      input.focus();
      input.setSelectionRange(caretPos, caretPos);
      buttonLabelSelectionRef.current = { start: caretPos, end: caretPos };
    });
  };

  switch (type) {
    case 'heading':
      return (
        <>
          <div className="pagebuilder-field-row">
            <Form.Group style={{ flex: '0 0 140px' }}>
              <Form.Label>Level</Form.Label>
              <Form.Select value={content?.level || 'h2'} onChange={(e) => onUpdate('level', e.target.value)}>
                <option value="h1">H1</option>
                <option value="h2">H2</option>
                <option value="h3">H3</option>
                <option value="h4">H4</option>
                <option value="h5">H5</option>
                <option value="h6">H6</option>
              </Form.Select>
            </Form.Group>
            <Form.Group style={{ flex: 1 }}>
              <Form.Label>Text</Form.Label>
              <Form.Control value={content?.text || ''} onChange={(e) => onUpdate('text', e.target.value)} onFocus={() => onFocus?.('text')} placeholder="Heading text…" />
            </Form.Group>
          </div>
          <IconClassPicker
            value={content?.icon || ''}
            onChange={(nextIconClass) => onUpdate('icon', nextIconClass)}
            maxRows={2}
          />
        </>
      );

    case 'paragraph':
    case 'text':
      return (
        <Form.Group>
          <Form.Label>Text</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={type === 'text' ? (content?.body || '') : (content?.text || '')}
            onChange={(e) => onUpdate(type === 'text' ? 'body' : 'text', e.target.value)}
            onFocus={() => onFocus?.(type === 'text' ? 'body' : 'text')}
            placeholder="Paragraph text…"
          />
        </Form.Group>
      );

    case 'html':
      return (
        <Form.Group>
          <Form.Label>HTML</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={content?.html || ''}
            onChange={(e) => onUpdate('html', e.target.value)}
            onFocus={() => onFocus?.('html')}
            placeholder="<span>Your HTML here</span>"
            style={{ fontFamily: 'monospace', fontSize: '0.88rem' }}
          />
        </Form.Group>
      );

    case 'quote':
      return (
        <>
          <div className="pagebuilder-field-row">
            <Form.Group style={{ flex: 1 }}>
              <Form.Label>Quote Text</Form.Label>
              <Form.Control as="textarea" rows={2} value={content?.text || ''} onChange={(e) => onUpdate('text', e.target.value)} onFocus={() => onFocus?.('text')} placeholder="The quote…" />
            </Form.Group>
            <Form.Group style={{ flex: 1 }}>
              <Form.Label>Author</Form.Label>
              <Form.Control value={content?.author || ''} onChange={(e) => onUpdate('author', e.target.value)} onFocus={() => onFocus?.('author')} placeholder="Author name…" />
            </Form.Group>
          </div>
          <IconClassPicker
            value={content?.icon || ''}
            onChange={(nextIconClass) => onUpdate('icon', nextIconClass)}
            maxRows={2}
          />
        </>
      );

    case 'callout':
      return (
        <>
          <div className="pagebuilder-field-row mb-2">
            <Form.Group style={{ flex: '0 0 160px' }}>
              <Form.Label>Variant</Form.Label>
              <Form.Select value={content?.variant || 'info'} onChange={(e) => onUpdate('variant', e.target.value)}>
                <option value="info">ℹ️ Info</option>
                <option value="warning">⚠️ Warning</option>
                <option value="success">✅ Success</option>
                <option value="danger">🚨 Danger</option>
              </Form.Select>
            </Form.Group>
          </div>
          <IconClassPicker
            value={content?.icon || ''}
            onChange={(nextIconClass) => onUpdate('icon', nextIconClass)}
            maxRows={2}
          />
          <Form.Group>
            <Form.Label>Message</Form.Label>
            <Form.Control as="textarea" rows={2} value={content?.text || ''} onChange={(e) => onUpdate('text', e.target.value)} onFocus={() => onFocus?.('text')} placeholder="Callout message…" />
          </Form.Group>
        </>
      );

    case 'highlightBox':
      return (
        <>
          <Form.Group className="mb-2">
            <Form.Label>Color</Form.Label>
            <Form.Select value={content?.color || 'yellow'} onChange={(e) => onUpdate('color', e.target.value)}>
              <option value="yellow">Yellow</option>
              <option value="blue">Blue</option>
              <option value="green">Green</option>
              <option value="pink">Pink</option>
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Highlighted Text</Form.Label>
            <Form.Control as="textarea" rows={2} value={content?.text || ''} onChange={(e) => onUpdate('text', e.target.value)} onFocus={() => onFocus?.('text')} placeholder="Highlighted text…" />
          </Form.Group>
        </>
      );

    case 'badge': {
      const badgeItems = content?.items || [];
      return (
        <>
          {badgeItems.map((item, bIdx) => (
            <div key={item.id} className="pagebuilder-sublist-item mb-2" style={{ padding: '0.5rem' }}>
              <div className="pagebuilder-field-row mb-1">
                <Form.Control
                  value={item.text || ''}
                  onChange={(e) => onUpdate('items', badgeItems.map((x) => x.id === item.id ? { ...x, text: e.target.value } : x))}
                  placeholder={`Badge ${bIdx + 1} label…`}
                />
                <Form.Select
                  style={{ flex: '0 0 140px' }}
                  value={item.variant || 'primary'}
                  onChange={(e) => onUpdate('items', badgeItems.map((x) => x.id === item.id ? { ...x, variant: e.target.value } : x))}
                >
                  <option value="primary">Primary</option>
                  <option value="secondary">Secondary</option>
                  <option value="success">Success</option>
                  <option value="warning">Warning</option>
                  <option value="danger">Danger</option>
                  <option value="info">Info</option>
                </Form.Select>
                <button type="button" className="pagebuilder-block-action-btn pagebuilder-block-action-btn--danger" onClick={() => onUpdate('items', badgeItems.filter((x) => x.id !== item.id))} title="Remove"><i className="bi bi-trash" /></button>
              </div>
              <IconClassPicker
                value={item.icon || ''}
                label="Badge Icon (optional)"
                placeholder="Search badge icon..."
                maxRows={2}
                onChange={(nextIconClass) => onUpdate('items', badgeItems.map((x) => x.id === item.id ? { ...x, icon: nextIconClass } : x))}
              />
            </div>
          ))}
          <button type="button" className="pagebuilder-add-btn pagebuilder-add-btn--sm mt-1" onClick={() => onUpdate('items', [...badgeItems, { id: `badge-${Date.now()}`, text: '', variant: 'primary', icon: '' }])}>
            <i className="bi bi-plus-lg" /> Add Badge
          </button>
        </>
      );
    }

    case 'bulletList': {
      const listItems = content?.items || [];
      return (
        <>
          <Form.Group className="mb-2">
            <Form.Label>List Style</Form.Label>
            <Form.Select value={content?.style || 'bullet'} onChange={(e) => onUpdate('style', e.target.value)}>
              <option value="bullet">Bullet</option>
              <option value="numbered">Numbered</option>
              <option value="checklist">Checklist</option>
            </Form.Select>
          </Form.Group>
          {listItems.map((li, liIdx) => (
            <div key={li.id} className="pagebuilder-field-row mb-1">
              <Form.Control
                value={li.text || ''}
                onChange={(e) => onUpdate('items', listItems.map((x) => x.id === li.id ? { ...x, text: e.target.value } : x))}
                placeholder={`Item ${liIdx + 1}…`}
              />
              <button type="button" className="pagebuilder-block-action-btn pagebuilder-block-action-btn--danger" onClick={() => onUpdate('items', listItems.filter((x) => x.id !== li.id))} title="Remove"><i className="bi bi-trash" /></button>
            </div>
          ))}
          <button type="button" className="pagebuilder-add-btn pagebuilder-add-btn--sm mt-1" onClick={() => onUpdate('items', [...listItems, { id: `li-${Date.now()}`, text: '' }])}>
            <i className="bi bi-plus-lg" /> Add Item
          </button>
        </>
      );
    }

    case 'iconList': {
      const listItems = content?.items || [];
      return (
        <>
          {listItems.map((li, liIdx) => (
            <div key={li.id} className="pagebuilder-field-row mb-1">
              <Form.Control
                style={{ flex: '0 0 180px' }}
                value={li.icon || ''}
                onChange={(e) => onUpdate('items', listItems.map((x) => x.id === li.id ? { ...x, icon: e.target.value } : x))}
                placeholder="bi bi-check-circle"
              />
              <Form.Control
                value={li.text || ''}
                onChange={(e) => onUpdate('items', listItems.map((x) => x.id === li.id ? { ...x, text: e.target.value } : x))}
                placeholder={`Item ${liIdx + 1}…`}
              />
              <button type="button" className="pagebuilder-block-action-btn pagebuilder-block-action-btn--danger" onClick={() => onUpdate('items', listItems.filter((x) => x.id !== li.id))} title="Remove"><i className="bi bi-trash" /></button>
            </div>
          ))}
          <button type="button" className="pagebuilder-add-btn pagebuilder-add-btn--sm mt-1" onClick={() => onUpdate('items', [...listItems, { id: `ili-${Date.now()}`, icon: 'bi bi-check-circle', text: '' }])}>
            <i className="bi bi-plus-lg" /> Add Item
          </button>
        </>
      );
    }

    case 'faq': {
      const faqItems = content?.items || [];
      return (
        <>
          {faqItems.map((faqItem, fIdx) => (
            <div key={faqItem.id} className="pagebuilder-sublist-item mb-2" style={{ padding: '0.5rem' }}>
              <div className="pagebuilder-sublist-item-header">
                <span className="pagebuilder-sublist-index">Q {fIdx + 1}</span>
                <button type="button" className="pagebuilder-block-action-btn pagebuilder-block-action-btn--danger" onClick={() => onUpdate('items', faqItems.filter((x) => x.id !== faqItem.id))} title="Remove"><i className="bi bi-trash" /></button>
              </div>
              <Form.Group className="mb-1">
                <Form.Label>Question</Form.Label>
                <Form.Control value={faqItem.question || ''} onChange={(e) => onUpdate('items', faqItems.map((x) => x.id === faqItem.id ? { ...x, question: e.target.value } : x))} placeholder="Question…" />
              </Form.Group>
              <Form.Group>
                <Form.Label>Answer</Form.Label>
                <Form.Control as="textarea" rows={2} value={faqItem.answer || ''} onChange={(e) => onUpdate('items', faqItems.map((x) => x.id === faqItem.id ? { ...x, answer: e.target.value } : x))} placeholder="Answer…" />
              </Form.Group>
              <IconClassPicker
                value={faqItem.icon || ''}
                label="FAQ Icon (optional)"
                placeholder="Search FAQ icon..."
                maxRows={2}
                onChange={(nextIconClass) => onUpdate('items', faqItems.map((x) => x.id === faqItem.id ? { ...x, icon: nextIconClass } : x))}
              />
            </div>
          ))}
          <button type="button" className="pagebuilder-add-btn pagebuilder-add-btn--sm" onClick={() => onUpdate('items', [...faqItems, { id: `faq-${Date.now()}`, question: '', answer: '', icon: '' }])}>
            <i className="bi bi-plus-lg" /> Add Q&amp;A
          </button>
        </>
      );
    }

    case 'toggleBlock':
      return (
        <>
          <Form.Group className="mb-2">
            <Form.Label>Toggle Label</Form.Label>
            <Form.Control value={content?.label || ''} onChange={(e) => onUpdate('label', e.target.value)} placeholder="Click to expand…" />
          </Form.Group>
          <Form.Group>
            <Form.Label>Content</Form.Label>
            <Form.Control as="textarea" rows={3} value={content?.content || ''} onChange={(e) => onUpdate('content', e.target.value)} placeholder="Content shown when expanded…" />
          </Form.Group>
        </>
      );

    case 'image':
      return (
        <>
          <Form.Group className="mb-2">
            <Form.Label>Image URL</Form.Label>
            <Form.Control value={content?.url || ''} onChange={(e) => onUpdate('url', e.target.value)} placeholder="https://…" />
          </Form.Group>
          <div className="pagebuilder-field-row">
            <Form.Group style={{ flex: 1 }}>
              <Form.Label>Alt Text</Form.Label>
              <Form.Control value={content?.alt || ''} onChange={(e) => onUpdate('alt', e.target.value)} placeholder="Describe the image" />
            </Form.Group>
            <Form.Group style={{ flex: 1 }}>
              <Form.Label>Caption</Form.Label>
              <Form.Control value={content?.caption || ''} onChange={(e) => onUpdate('caption', e.target.value)} placeholder="Optional caption" />
            </Form.Group>
          </div>
        </>
      );

    case 'video':
      return (
        <>
          <Form.Group className="mb-2">
            <Form.Label>Embed URL</Form.Label>
            <Form.Control value={content?.embedUrl || ''} onChange={(e) => onUpdate('embedUrl', e.target.value)} placeholder="YouTube or Vimeo URL" />
          </Form.Group>
          <div className="pagebuilder-field-row">
            <Form.Group style={{ flex: 1 }}>
              <Form.Label>Title</Form.Label>
              <Form.Control value={content?.title || ''} onChange={(e) => onUpdate('title', e.target.value)} placeholder="Video title" />
            </Form.Group>
            <Form.Group style={{ flex: 1 }}>
              <Form.Label>Caption</Form.Label>
              <Form.Control value={content?.caption || ''} onChange={(e) => onUpdate('caption', e.target.value)} placeholder="Optional caption" />
            </Form.Group>
          </div>
        </>
      );

    case 'audio':
      return (
        <>
          <Form.Group className="mb-2">
            <Form.Label>Audio URL</Form.Label>
            <Form.Control value={content?.url || ''} onChange={(e) => onUpdate('url', e.target.value)} placeholder="https://…/audio.mp3" />
            <Form.Text className="pagebuilder-help-note">Direct link to an MP3, OGG, or WAV file.</Form.Text>
          </Form.Group>
          <div className="pagebuilder-field-row">
            <Form.Group style={{ flex: 1 }}>
              <Form.Label>Title</Form.Label>
              <Form.Control value={content?.title || ''} onChange={(e) => onUpdate('title', e.target.value)} placeholder="Audio title" />
            </Form.Group>
            <Form.Group style={{ flex: 1 }}>
              <Form.Label>Caption</Form.Label>
              <Form.Control value={content?.caption || ''} onChange={(e) => onUpdate('caption', e.target.value)} placeholder="Optional caption" />
            </Form.Group>
          </div>
        </>
      );

    case 'pdf':
      return (
        <>
          <Form.Group className="mb-2">
            <Form.Label>PDF URL</Form.Label>
            <Form.Control value={content?.url || ''} onChange={(e) => onUpdate('url', e.target.value)} placeholder="https://…/document.pdf" />
          </Form.Group>
          <div className="pagebuilder-field-row">
            <Form.Group style={{ flex: 1 }}>
              <Form.Label>Title</Form.Label>
              <Form.Control value={content?.title || ''} onChange={(e) => onUpdate('title', e.target.value)} placeholder="Document title" />
            </Form.Group>
            <Form.Group style={{ flex: '0 0 180px' }}>
              <Form.Label>Viewer height (px)</Form.Label>
              <Form.Control type="number" min={200} max={1200} value={content?.height || 480} onChange={(e) => onUpdate('height', Number(e.target.value))} />
            </Form.Group>
          </div>
        </>
      );

    case 'button':
      return (
        <>
          <div className="pagebuilder-field-row mb-2">
            <Form.Group style={{ flex: 1 }}>
              <Form.Label>Label</Form.Label>
              <Form.Control
                ref={buttonLabelInputRef}
                value={content?.label || ''}
                onChange={(e) => onUpdate('label', e.target.value)}
                onFocus={(e) => {
                  captureButtonLabelSelection(e);
                  onFocus?.('label');
                }}
                onClick={captureButtonLabelSelection}
                onSelect={captureButtonLabelSelection}
                onKeyUp={captureButtonLabelSelection}
                placeholder="Button text"
              />
            </Form.Group>
            <Form.Group style={{ flex: 1 }}>
              <Form.Label>URL</Form.Label>
              <Form.Control value={content?.url || ''} onChange={(e) => onUpdate('url', e.target.value)} onFocus={() => onFocus?.('url')} placeholder="https://…" />
            </Form.Group>
          </div>
          <div className="pagebuilder-field-row mb-2">
            <Form.Group style={{ flex: '0 0 180px' }}>
              <Form.Label>Variant</Form.Label>
              <Form.Select value={content?.variant || 'primary'} onChange={(e) => onUpdate('variant', e.target.value)}>
                <option value="primary">Primary</option>
                <option value="secondary">Secondary</option>
                <option value="success">Success</option>
                <option value="warning">Warning</option>
                <option value="danger">Danger</option>
                <option value="light">Light</option>
                <option value="dark">Dark</option>
              </Form.Select>
            </Form.Group>
            <Form.Group style={{ flex: '0 0 160px' }}>
              <Form.Label>Size</Form.Label>
              <Form.Select value={content?.size || 'md'} onChange={(e) => onUpdate('size', e.target.value)}>
                <option value="sm">Small</option>
                <option value="md">Medium</option>
                <option value="lg">Large</option>
              </Form.Select>
            </Form.Group>
          </div>
          <IconClassPicker
            value={content?.icon || ''}
            onChange={(nextIconClass) => {
              onUpdate('icon', nextIconClass);
              insertIconClassIntoButtonLabel(nextIconClass);
            }}
            maxRows={2}
          />
          <Form.Group className="mb-2">
            <Form.Label>Link to Page Builder Page</Form.Label>
            <Form.Select
              value={typeof content?.url === 'string' && content.url.startsWith('#pb-page:') ? content.url.slice('#pb-page:'.length) : ''}
              onChange={(e) => {
                const pageId = e.target.value;
                if (!pageId) return;
                onUpdate('url', `#pb-page:${pageId}`);
              }}
            >
              <option value="">Select page...</option>
              {pageOptions.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Check
            type="switch"
            id={`nested-btn-newtab-${blockId}`}
            label="Open in new tab"
            checked={content?.openNewTab === true}
            onChange={(e) => onUpdate('openNewTab', e.target.checked)}
          />
        </>
      );

    case 'agendaSnippet':
      return (
        <div className="pagebuilder-field-row">
          <Form.Group style={{ flex: 1 }}>
            <Form.Label>Label</Form.Label>
            <Form.Control value={content?.label || ''} onChange={(e) => onUpdate('label', e.target.value)} placeholder="e.g. Next Meeting Agenda" />
          </Form.Group>
          <Form.Group style={{ flex: 1 }}>
            <Form.Label>Meeting ID</Form.Label>
            <Form.Control value={content?.meetingId || ''} onChange={(e) => onUpdate('meetingId', e.target.value)} placeholder="Meeting ID" />
          </Form.Group>
        </div>
      );

    case 'announcement':
      return (
        <>
          <div className="pagebuilder-field-row mb-2">
            <Form.Group style={{ flex: '0 0 160px' }}>
              <Form.Label>Variant</Form.Label>
              <Form.Select value={content?.variant || 'info'} onChange={(e) => onUpdate('variant', e.target.value)}>
                <option value="info">ℹ️ Info</option>
                <option value="warning">⚠️ Warning</option>
                <option value="success">✅ Success</option>
                <option value="danger">🚨 Danger</option>
              </Form.Select>
            </Form.Group>
          </div>
          <IconClassPicker
            value={content?.icon || ''}
            onChange={(nextIconClass) => onUpdate('icon', nextIconClass)}
            maxRows={2}
          />
          <Form.Group className="mb-2">
            <Form.Label>Message</Form.Label>
            <Form.Control as="textarea" rows={2} value={content?.text || ''} onChange={(e) => onUpdate('text', e.target.value)} placeholder="Announcement text…" />
          </Form.Group>
          <Form.Check
            type="switch"
            id={`nested-ann-dismiss-${blockId}`}
            label="Show dismiss button"
            checked={content?.dismissible !== false}
            onChange={(e) => onUpdate('dismissible', e.target.checked)}
          />
        </>
      );

    case 'countdown':
      return (
        <>
          <Form.Group className="mb-2">
            <Form.Label>Title</Form.Label>
            <Form.Control value={content?.title || ''} onChange={(e) => onUpdate('title', e.target.value)} placeholder="Countdown title…" />
          </Form.Group>
          <IconClassPicker
            value={content?.icon || ''}
            onChange={(nextIconClass) => onUpdate('icon', nextIconClass)}
            maxRows={2}
          />
          <Form.Group className="mb-2">
            <Form.Label>Target Date &amp; Time</Form.Label>
            <Form.Control type="datetime-local" value={content?.targetDate || ''} onChange={(e) => onUpdate('targetDate', e.target.value)} />
          </Form.Group>
          <Form.Group>
            <Form.Label>Completed Message</Form.Label>
            <Form.Control value={content?.completedMessage || ''} onChange={(e) => onUpdate('completedMessage', e.target.value)} placeholder="Time is up!" />
          </Form.Group>
        </>
      );

    case 'poll': {
      const pollOptions = content?.options || [];
      return (
        <>
          <Form.Group className="mb-2">
            <Form.Label>Question</Form.Label>
            <Form.Control value={content?.question || ''} onChange={(e) => onUpdate('question', e.target.value)} placeholder="Your question…" />
          </Form.Group>
          {pollOptions.map((opt, pIdx) => (
            <div key={opt.id} className="pagebuilder-field-row mb-1">
              <Form.Control
                value={opt.text || ''}
                onChange={(e) => onUpdate('options', pollOptions.map((x) => x.id === opt.id ? { ...x, text: e.target.value } : x))}
                placeholder={`Option ${pIdx + 1}…`}
              />
              <button type="button" className="pagebuilder-block-action-btn pagebuilder-block-action-btn--danger" onClick={() => onUpdate('options', pollOptions.filter((x) => x.id !== opt.id))} title="Remove"><i className="bi bi-trash" /></button>
            </div>
          ))}
          <button type="button" className="pagebuilder-add-btn pagebuilder-add-btn--sm mt-1 mb-2" onClick={() => onUpdate('options', [...pollOptions, { id: `opt-${Date.now()}`, text: '' }])}>
            <i className="bi bi-plus-lg" /> Add Option
          </button>
          <Form.Check
            type="switch"
            id={`nested-poll-multi-${blockId}`}
            label="Allow multiple selections"
            checked={content?.allowMultiple === true}
            onChange={(e) => onUpdate('allowMultiple', e.target.checked)}
          />
        </>
      );
    }

    case 'divider':
      return <p className="pagebuilder-help-note">Visual divider — no settings needed.</p>;

    default:
      return (
        <p className="pagebuilder-help-note">
          <i className="bi bi-info-circle me-1" />
          This block type is best used at the top level. Select a simpler type for nested content.
        </p>
      );
  }
}

export default function LayoutEditor({ type, content, update, setContent }) {
  const pageOptions = usePageBuilderPages();
  const [showRefLegend, setShowRefLegend] = useState(false);
  const [refSearch, setRefSearch] = useState('');
  const [activeTokenTarget, setActiveTokenTarget] = useState(null);

  const filteredRefTokens = useMemo(() => {
    const q = refSearch.trim().toLowerCase();
    if (!q) return DATA_REFERENCE_TOKENS;
    return DATA_REFERENCE_TOKENS.filter((item) => (`${item.label} ${item.token} ${item.group}`).toLowerCase().includes(q));
  }, [refSearch]);

  const insertToken = (token) => {
    if (!activeTokenTarget || !token) return;

    if (activeTokenTarget === 'hero:title') {
      update('title', appendToken(content.title || '', token));
      return;
    }

    if (activeTokenTarget === 'hero:subtitle') {
      update('subtitle', appendToken(content.subtitle || '', token));
      return;
    }

    if (activeTokenTarget === 'card:title') {
      update('title', appendToken(content.title || '', token));
      return;
    }

    if (activeTokenTarget === 'card:subtitle') {
      update('subtitle', appendToken(content.subtitle || '', token));
      return;
    }

    if (activeTokenTarget.startsWith('card:nested:')) {
      const [, , nestedId, field] = activeTokenTarget.split(':');
      update('blocks', (content.blocks || []).map((block) => (
        block.id === nestedId
          ? { ...block, content: { ...(block.content || {}), [field]: appendToken(block.content?.[field] || '', token) } }
          : block
      )));
      return;
    }

    if (activeTokenTarget.startsWith('acc:nested:')) {
      // acc:nested:{itemId}:{blockId}:{field}
      const parts = activeTokenTarget.split(':');
      const [, , itemId, blockId, field] = parts;
      update('items', (content.items || []).map((item) =>
        item.id === itemId
          ? {
            ...item,
            blocks: (item.blocks || []).map((b) =>
              b.id === blockId
                ? { ...b, content: { ...(b.content || {}), [field]: appendToken(b.content?.[field] || '', token) } }
                : b
            ),
          }
          : item
      ));
    }
  };

  switch (type) {

    case 'hero':
      return (
        <div className="pagebuilder-editor-fields">
          <TextFormatButtons />
          <div className="pagebuilder-field-row mb-3">
            <Form.Group style={{ flex: '0 0 56px' }}>
              <Form.Control
                type="color"
                value={content.titleColor || '#ffffff'}
                onChange={(e) => update('titleColor', e.target.value)}
                title="Pick title color"
                aria-label="Title text color"
              />
            </Form.Group>
            <Form.Group style={{ flex: 1 }}>
              <Form.Control
                value={content.title || ''}
                onChange={(e) => update('title', e.target.value)}
                onFocus={() => setActiveTokenTarget('hero:title')}
                placeholder="Hero title…"
                aria-label="Hero title"
              />
            </Form.Group>
          </div>
          <div className="pagebuilder-field-row mb-3">
            <Form.Group style={{ flex: '0 0 56px' }}>
              <Form.Control
                type="color"
                value={content.subtitleColor || '#ffffff'}
                onChange={(e) => update('subtitleColor', e.target.value)}
                title="Pick subtitle color"
                aria-label="Subtitle text color"
              />
            </Form.Group>
            <Form.Group style={{ flex: 1 }}>
              <Form.Control
                value={content.subtitle || ''}
                onChange={(e) => update('subtitle', e.target.value)}
                onFocus={() => setActiveTokenTarget('hero:subtitle')}
                placeholder="Subtitle…"
                aria-label="Hero subtitle"
              />
            </Form.Group>
          </div>
          <TokenInsertButtons onInsert={insertToken} />
          <HeroImageBuilder content={content} update={update} />
        </div>
      );

    case 'banner':
      return (
        <div className="pagebuilder-editor-fields">
          <TextFormatButtons />
          <div className="pagebuilder-field-row mb-3">
            <Form.Group style={{ flex: 1 }}>
              <Form.Label>Title</Form.Label>
              <Form.Control value={content.title || ''} onChange={(e) => update('title', e.target.value)} placeholder="Banner title…" />
            </Form.Group>
          </div>
          <Form.Group className="mb-3">
            <Form.Label>Subtitle</Form.Label>
            <Form.Control value={content.subtitle || ''} onChange={(e) => update('subtitle', e.target.value)} placeholder="Subtitle…" />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Background Image URL</Form.Label>
            <Form.Control value={content.backgroundUrl || ''} onChange={(e) => update('backgroundUrl', e.target.value)} placeholder="https://…" />
            <Form.Text className="pagebuilder-help-note">
              Recommended: wide panoramic image around 3:1 (for example 2400x800px; minimum 1800x600px).
            </Form.Text>
          </Form.Group>
          <div className="pagebuilder-field-row mb-3">
            <Form.Group style={{ flex: 1 }}>
              <Form.Label>CTA Button Label</Form.Label>
              <Form.Control value={content.ctaLabel || ''} onChange={(e) => update('ctaLabel', e.target.value)} placeholder="e.g. Learn More" />
            </Form.Group>
            <Form.Group style={{ flex: 1 }}>
              <Form.Label>CTA URL</Form.Label>
              <Form.Control value={content.ctaUrl || ''} onChange={(e) => update('ctaUrl', e.target.value)} placeholder="https://…" />
            </Form.Group>
          </div>
          <Form.Group>
            <Form.Label>Overlay opacity ({Math.round((content.overlayOpacity ?? 0.4) * 100)}%)</Form.Label>
            <Form.Range min={0} max={1} step={0.05} value={content.overlayOpacity ?? 0.4} onChange={(e) => update('overlayOpacity', parseFloat(e.target.value))} />
          </Form.Group>
        </div>
      );

    case 'columns': {
      const count = content.count || 2;
      const cols = content.cols || [];

      const handleCountChange = (newCount) => {
        const n = Number(newCount);
        let newCols = [...cols];
        while (newCols.length < n) newCols.push({ id: `col-${Date.now()}-${newCols.length}`, title: '', content: '' });
        if (newCols.length > n) newCols = newCols.slice(0, n);
        setContent({ ...content, count: n, cols: newCols });
      };

      const updateCol = (cid, field, val) => update('cols', cols.map((c) => c.id === cid ? { ...c, [field]: val } : c));

      return (
        <div className="pagebuilder-editor-fields">
          <TextFormatButtons />
          <Form.Group className="mb-3">
            <Form.Label>Number of columns</Form.Label>
            <Form.Select value={count} onChange={(e) => handleCountChange(e.target.value)}>
              <option value={2}>2 Columns</option>
              <option value={3}>3 Columns</option>
            </Form.Select>
          </Form.Group>
          <div className="pagebuilder-cols-editor">
            {cols.map((col, idx) => (
              <div key={col.id} className="pagebuilder-col-editor">
                <p className="pagebuilder-sublist-index mb-2">Column {idx + 1}</p>
                <Form.Group className="mb-2">
                  <Form.Label>Title</Form.Label>
                  <Form.Control value={col.title} onChange={(e) => updateCol(col.id, 'title', e.target.value)} placeholder={`Column ${idx + 1} title…`} />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Content</Form.Label>
                  <Form.Control as="textarea" rows={4} value={col.content} onChange={(e) => updateCol(col.id, 'content', e.target.value)} placeholder="Column content…" />
                </Form.Group>
              </div>
            ))}
          </div>
        </div>
      );
    }

    case 'cardGrid': {
      const cards = content.cards || [];
      const addCard = () => update('cards', [...cards, { id: `card-${Date.now()}`, title: '', titleLevel: 'h5', body: '', imageUrl: '', buttonLabel: '', buttonUrl: '', buttonVariant: 'primary', icon: '' }]);
      const removeCard = (cid) => update('cards', cards.filter((c) => c.id !== cid));
      const updateCard = (cid, field, val) => update('cards', cards.map((c) => c.id === cid ? { ...c, [field]: val } : c));
      return (
        <div className="pagebuilder-editor-fields">
          <TextFormatButtons />
          <div className="pagebuilder-field-row mb-3">
            <Form.Group style={{ flex: '0 0 180px' }}>
              <Form.Label>Columns per row</Form.Label>
              <Form.Select value={content.columns || 3} onChange={(e) => update('columns', Number(e.target.value))}>
                <option value={2}>2</option>
                <option value={3}>3</option>
                <option value={4}>4</option>
              </Form.Select>
            </Form.Group>
          </div>
          <div className="pagebuilder-sublist-header">
            <span className="pagebuilder-sublist-title">Cards ({cards.length})</span>
            <button type="button" className="pagebuilder-add-btn pagebuilder-add-btn--sm" onClick={addCard}>
              <i className="bi bi-plus-lg" aria-hidden="true" /> Add Card
            </button>
          </div>
          {cards.length === 0 && <p className="pagebuilder-sublist-empty">No cards yet.</p>}
          <div className="pagebuilder-cardgrid-editor">
            {cards.map((card, idx) => (
              <div key={card.id} className="pagebuilder-card-editor">
                <div className="pagebuilder-collage-item-header">
                  <span className="pagebuilder-sublist-index">Card {idx + 1}</span>
                  <button type="button" className="pagebuilder-block-action-btn pagebuilder-block-action-btn--danger" onClick={() => removeCard(card.id)} title="Remove"><i className="bi bi-trash" /></button>
                </div>
                <Form.Group className="mb-2">
                  <Form.Label>Image URL</Form.Label>
                  <Form.Control size="sm" value={card.imageUrl} onChange={(e) => updateCard(card.id, 'imageUrl', e.target.value)} placeholder="https://…" />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Heading</Form.Label>
                  <Form.Select size="sm" value={card.titleLevel || 'h5'} onChange={(e) => updateCard(card.id, 'titleLevel', e.target.value)}>
                    {HEADING_LEVELS.map((level) => <option key={level} value={level}>{level.toUpperCase()}</option>)}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Title</Form.Label>
                  <Form.Control size="sm" value={card.title} onChange={(e) => updateCard(card.id, 'title', e.target.value)} placeholder="Card title…" />
                </Form.Group>
                <IconClassPicker
                  value={card.icon || ''}
                  label="Card Icon (optional)"
                  placeholder="Search card icon..."
                  maxRows={2}
                  onChange={(nextIconClass) => updateCard(card.id, 'icon', nextIconClass)}
                />
                <Form.Group className="mb-2">
                  <Form.Label>Body</Form.Label>
                  <Form.Control size="sm" as="textarea" rows={2} value={card.body} onChange={(e) => updateCard(card.id, 'body', e.target.value)} placeholder="Card body…" />
                </Form.Group>
                <div className="pagebuilder-field-row">
                  <Form.Group style={{ flex: 1 }}>
                    <Form.Label>Button Label</Form.Label>
                    <Form.Control size="sm" value={card.buttonLabel} onChange={(e) => updateCard(card.id, 'buttonLabel', e.target.value)} placeholder="Read more…" />
                  </Form.Group>
                  <Form.Group style={{ flex: 1 }}>
                    <Form.Label>Button URL</Form.Label>
                    <Form.Control size="sm" value={card.buttonUrl} onChange={(e) => updateCard(card.id, 'buttonUrl', e.target.value)} placeholder="https://…" />
                  </Form.Group>
                </div>
                <Form.Group className="mt-2">
                  <Form.Label>Button Color</Form.Label>
                  <Form.Select size="sm" value={card.buttonVariant || 'primary'} onChange={(e) => updateCard(card.id, 'buttonVariant', e.target.value)}>
                    {BOOTSTRAP_VARIANTS.map((variant) => <option key={variant} value={variant}>{variant.charAt(0).toUpperCase() + variant.slice(1)}</option>)}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mt-2">
                  <Form.Label>Link to Page Builder Page</Form.Label>
                  <Form.Select
                    size="sm"
                    value={typeof card.buttonUrl === 'string' && card.buttonUrl.startsWith('#pb-page:') ? card.buttonUrl.slice('#pb-page:'.length) : ''}
                    onChange={(e) => updateCard(card.id, 'buttonUrl', e.target.value ? `#pb-page:${e.target.value}` : '')}
                  >
                    <option value="">Select page...</option>
                    {pageOptions.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
                  </Form.Select>
                </Form.Group>
              </div>
            ))}
          </div>
        </div>
      );
    }

    case 'dataCard': {
      const blocks = content.blocks || [];
      const addBlock = () => update('blocks', [...blocks, makeDataCardBlock('paragraph')]);
      const removeBlock = (blockId) => update('blocks', blocks.filter((b) => b.id !== blockId));
      const moveBlock = (blockId, dir) => {
        const idx = blocks.findIndex((b) => b.id === blockId);
        const nextIdx = idx + dir;
        if (idx < 0 || nextIdx < 0 || nextIdx >= blocks.length) return;
        const next = [...blocks];
        [next[idx], next[nextIdx]] = [next[nextIdx], next[idx]];
        update('blocks', next);
      };
      const updateNestedType = (blockId, nextType) => {
        update('blocks', blocks.map((b) => (b.id === blockId
          ? { ...b, type: nextType, content: nextType === 'html' ? { html: '' } : defaultContent(nextType) }
          : b)));
      };
      const updateNestedContent = (blockId, key, val) => {
        update('blocks', blocks.map((b) => (b.id === blockId ? { ...b, content: { ...(b.content || {}), [key]: val } } : b)));
      };

      return (
        <div className="pagebuilder-editor-fields">
          <TextFormatButtons />
          <div className="pagebuilder-ref-legend-wrap">
            <button
              type="button"
              className="pagebuilder-block-action-btn"
              onClick={() => setShowRefLegend((prev) => !prev)}
              title="Toggle references legend"
            >
              <i className="bi bi-info-circle" /> References
            </button>

            {showRefLegend && (
              <div className="pagebuilder-ref-legend-panel">
                <Form.Control
                  size="sm"
                  type="search"
                  placeholder="Search references..."
                  value={refSearch}
                  onChange={(e) => setRefSearch(e.target.value)}
                />
                <div className="pagebuilder-ref-legend-list">
                  {filteredRefTokens.map((item) => (
                    <div key={item.token} className="pagebuilder-ref-legend-item">
                      <strong>{item.label}</strong>
                      <span>{item.token}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <datalist id={REF_TOKEN_DATALIST_ID}>
            {DATA_REFERENCE_TOKENS.map((item) => (
              <option key={item.token} value={item.token}>{item.label}</option>
            ))}
          </datalist>

          <div className="pagebuilder-field-row mb-3">
            <Form.Group style={{ flex: '0 0 100px' }}>
              <Form.Label>Heading</Form.Label>
              <Form.Select value={content.titleLevel || 'h3'} onChange={(e) => update('titleLevel', e.target.value)}>
                {HEADING_LEVELS.map((level) => <option key={level} value={level}>{level.toUpperCase()}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group style={{ flex: 1 }}>
              <Form.Label>Card Title</Form.Label>
              <Form.Control list={REF_TOKEN_DATALIST_ID} value={content.title || ''} onChange={(e) => update('title', e.target.value)} onFocus={() => setActiveTokenTarget('card:title')} placeholder="Meeting information" />
            </Form.Group>
            <Form.Group style={{ flex: 1 }}>
              <Form.Label>Card Subtitle</Form.Label>
              <Form.Control list={REF_TOKEN_DATALIST_ID} value={content.subtitle || ''} onChange={(e) => update('subtitle', e.target.value)} onFocus={() => setActiveTokenTarget('card:subtitle')} placeholder="Everything in one place" />
            </Form.Group>
          </div>
          <IconClassPicker
            value={content.icon || ''}
            label="Card Icon (optional)"
            placeholder="Search card icon..."
            maxRows={2}
            onChange={(nextIconClass) => update('icon', nextIconClass)}
          />

          <div className="pagebuilder-sublist-header">
            <span className="pagebuilder-sublist-title">Nested Blocks ({blocks.length})</span>
            <button type="button" className="pagebuilder-add-btn pagebuilder-add-btn--sm" onClick={addBlock}>
              <i className="bi bi-plus-lg" aria-hidden="true" /> Add Block
            </button>
          </div>

          <Form.Text className="pagebuilder-help-note">
            Use references like {'{{club.name}}'}, {'{{meeting.dayOfWeek}}'}, {'{{meeting.location}}'}, {'{{meeting.nextMeetingLink}}'} inside text, headings, buttons, and HTML.
          </Form.Text>

          {blocks.length === 0 && <p className="pagebuilder-sublist-empty">No nested blocks yet.</p>}

          {blocks.map((nested, idx) => (
            <div key={nested.id} className="pagebuilder-sublist-item">
              <div className="pagebuilder-sublist-item-header">
                <span className="pagebuilder-sublist-index">Block {idx + 1}</span>
                <div className="pagebuilder-block-actions">
                  <button type="button" className="pagebuilder-block-action-btn" onClick={() => moveBlock(nested.id, -1)} disabled={idx === 0} title="Move up"><i className="bi bi-arrow-up" /></button>
                  <button type="button" className="pagebuilder-block-action-btn" onClick={() => moveBlock(nested.id, 1)} disabled={idx === blocks.length - 1} title="Move down"><i className="bi bi-arrow-down" /></button>
                  <button type="button" className="pagebuilder-block-action-btn pagebuilder-block-action-btn--danger" onClick={() => removeBlock(nested.id)} title="Remove"><i className="bi bi-trash" /></button>
                </div>
              </div>

              <div className="pagebuilder-field-row mb-2">
                <Form.Group style={{ flex: '0 0 220px' }}>
                  <Form.Label>Block Type</Form.Label>
                  <Form.Select value={nested.type || 'paragraph'} onChange={(e) => updateNestedType(nested.id, e.target.value)}>
                    {DATA_CARD_BLOCK_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </Form.Select>
                </Form.Group>
                <Form.Group style={{ flex: '0 0 160px' }}>
                  <Form.Label>Alignment</Form.Label>
                  <Form.Select value={nested.content?.align || 'left'} onChange={(e) => updateNestedContent(nested.id, 'align', e.target.value)}>
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                  </Form.Select>
                </Form.Group>
              </div>

              {nested.type !== 'button' && <TextFormatButtons />}

              <NestedBlockFields
                type={nested.type}
                content={nested.content}
                blockId={nested.id}
                onUpdate={(key, val) => updateNestedContent(nested.id, key, val)}
                onFocus={(field) => setActiveTokenTarget(`card:nested:${nested.id}:${field}`)}
              />
            </div>
          ))}
          <TokenInsertButtons onInsert={insertToken} />
        </div>
      );
    }

    case 'accordion': {
      const items = content.items || [];
      const addItem = () => update('items', [...items, { id: `acc-${Date.now()}-${Math.floor(Math.random() * 10000)}`, title: '', blocks: [] }]);
      const removeItem = (itemId) => update('items', items.filter((i) => i.id !== itemId));
      const updateItemTitle = (itemId, newTitle) => update('items', items.map((i) => (i.id === itemId ? { ...i, title: newTitle } : i)));
      const moveItem = (itemId, dir) => {
        const idx = items.findIndex((i) => i.id === itemId);
        const nextIdx = idx + dir;
        if (idx < 0 || nextIdx < 0 || nextIdx >= items.length) return;
        const next = [...items];
        [next[idx], next[nextIdx]] = [next[nextIdx], next[idx]];
        update('items', next);
      };
      const addBlockToItem = (itemId) => {
        update('items', items.map((i) => (i.id === itemId ? { ...i, blocks: [...(i.blocks || []), makeDataCardBlock('paragraph')] } : i)));
      };
      const removeBlockFromItem = (itemId, blockId) => {
        update('items', items.map((i) => (i.id === itemId ? { ...i, blocks: (i.blocks || []).filter((b) => b.id !== blockId) } : i)));
      };
      const moveBlockInItem = (itemId, blockId, dir) => {
        update('items', items.map((i) => {
          if (i.id !== itemId) return i;
          const blocks = i.blocks || [];
          const idx = blocks.findIndex((b) => b.id === blockId);
          const nextIdx = idx + dir;
          if (idx < 0 || nextIdx < 0 || nextIdx >= blocks.length) return i;
          const next = [...blocks];
          [next[idx], next[nextIdx]] = [next[nextIdx], next[idx]];
          return { ...i, blocks: next };
        }));
      };
      const updateBlockInItem = (itemId, blockId, key, val) => {
        update('items', items.map((i) => (i.id === itemId ? { ...i, blocks: (i.blocks || []).map((b) => (b.id === blockId ? { ...b, content: { ...(b.content || {}), [key]: val } } : b)) } : i)));
      };
      const updateBlockTypeInItem = (itemId, blockId, nextType) => {
        update('items', items.map((i) => (i.id === itemId ? { ...i, blocks: (i.blocks || []).map((b) => (b.id === blockId ? { ...b, type: nextType, content: nextType === 'html' ? { html: '' } : defaultContent(nextType) } : b)) } : i)));
      };

      return (
        <div className="pagebuilder-editor-fields">
          <TextFormatButtons />
          <div className="pagebuilder-sublist-header">
            <span className="pagebuilder-sublist-title">Accordion Panels ({items.length})</span>
            <button type="button" className="pagebuilder-add-btn pagebuilder-add-btn--sm" onClick={addItem}>
              <i className="bi bi-plus-lg" aria-hidden="true" /> Add Panel
            </button>
          </div>

          {items.length === 0 && <p className="pagebuilder-sublist-empty">No panels yet.</p>}

          {items.map((item, itemIdx) => {
            const blocks = item.blocks || [];
            return (
              <div key={item.id} className="pagebuilder-sublist-item">
                <div className="pagebuilder-sublist-item-header">
                  <span className="pagebuilder-sublist-index">Panel {itemIdx + 1}</span>
                  <div className="pagebuilder-block-actions">
                    <button type="button" className="pagebuilder-block-action-btn" onClick={() => moveItem(item.id, -1)} disabled={itemIdx === 0} title="Move up"><i className="bi bi-arrow-up" /></button>
                    <button type="button" className="pagebuilder-block-action-btn" onClick={() => moveItem(item.id, 1)} disabled={itemIdx === items.length - 1} title="Move down"><i className="bi bi-arrow-down" /></button>
                    <button type="button" className="pagebuilder-block-action-btn pagebuilder-block-action-btn--danger" onClick={() => removeItem(item.id)} title="Remove"><i className="bi bi-trash" /></button>
                  </div>
                </div>

                <Form.Group className="mb-3">
                  <Form.Label>Panel Title</Form.Label>
                  <Form.Control value={item.title || ''} onChange={(e) => updateItemTitle(item.id, e.target.value)} placeholder="Click to expand..." />
                </Form.Group>

                <div className="pagebuilder-sublist-header" style={{ marginTop: '0.75rem' }}>
                  <span className="pagebuilder-sublist-title">Nested Blocks ({blocks.length})</span>
                  <button type="button" className="pagebuilder-add-btn pagebuilder-add-btn--sm" onClick={() => addBlockToItem(item.id)}>
                    <i className="bi bi-plus-lg" aria-hidden="true" /> Add Block
                  </button>
                </div>

                {blocks.length === 0 && <p className="pagebuilder-sublist-empty">No blocks in this panel yet.</p>}

                {blocks.map((block, blockIdx) => (
                  <div key={block.id} className="pagebuilder-sublist-item" style={{ marginLeft: '1rem', marginTop: '0.5rem' }}>
                    <div className="pagebuilder-sublist-item-header">
                      <span className="pagebuilder-sublist-index">Block {blockIdx + 1}</span>
                      <div className="pagebuilder-block-actions">
                        <button type="button" className="pagebuilder-block-action-btn" onClick={() => moveBlockInItem(item.id, block.id, -1)} disabled={blockIdx === 0} title="Move up"><i className="bi bi-arrow-up" /></button>
                        <button type="button" className="pagebuilder-block-action-btn" onClick={() => moveBlockInItem(item.id, block.id, 1)} disabled={blockIdx === blocks.length - 1} title="Move down"><i className="bi bi-arrow-down" /></button>
                        <button type="button" className="pagebuilder-block-action-btn pagebuilder-block-action-btn--danger" onClick={() => removeBlockFromItem(item.id, block.id)} title="Remove"><i className="bi bi-trash" /></button>
                      </div>
                    </div>

                    <div className="pagebuilder-field-row mb-2">
                      <Form.Group style={{ flex: '0 0 220px' }}>
                        <Form.Label>Block Type</Form.Label>
                        <Form.Select value={block.type || 'paragraph'} onChange={(e) => updateBlockTypeInItem(item.id, block.id, e.target.value)}>
                          {DATA_CARD_BLOCK_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                        </Form.Select>
                      </Form.Group>
                      <Form.Group style={{ flex: '0 0 160px' }}>
                        <Form.Label>Alignment</Form.Label>
                        <Form.Select value={block.content?.align || 'left'} onChange={(e) => updateBlockInItem(item.id, block.id, 'align', e.target.value)}>
                          <option value="left">Left</option>
                          <option value="center">Center</option>
                          <option value="right">Right</option>
                        </Form.Select>
                      </Form.Group>
                    </div>

                    {block.type !== 'button' && <TextFormatButtons />}

                    <NestedBlockFields
                      type={block.type}
                      content={block.content}
                      blockId={block.id}
                      onUpdate={(key, val) => updateBlockInItem(item.id, block.id, key, val)}
                      onFocus={(field) => setActiveTokenTarget(`acc:nested:${item.id}:${block.id}:${field}`)}
                    />
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      );
    }

    default:
      return null;
  }
}
